import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import {
  ClockIcon,
  DollarSignIcon,
  HomeIcon,
  MessageSquareIcon,
  PieChartIcon,
  SettingsIcon,
  ThumbsUpIcon,
  UsersIcon,
} from 'lucide-react'
import type { IconComponent } from '@workwhile/web-ui'
import { PermissionEnum } from 'api/userPermissions'
import { CompanyInfo } from 'api/company'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { useAuthContext } from 'components/auth/useAuthContext'

export interface NavOption {
  to: string
  icon: IconComponent
  text: string
  permissionRequired?: PermissionEnum
  featureGate?: FeatureGates
  customCondition?: (companyInfo: CompanyInfo) => boolean
}

const navOptions: NavOption[] = [
  {
    to: '/home',
    text: 'Home',
    icon: HomeIcon,
  },
  {
    to: '/pulse',
    text: 'Pulse',
    icon: PieChartIcon,
    permissionRequired: PermissionEnum.PaymentAccess,
  },
  {
    to: '/vndly',
    text: 'VNDLY',
    icon: ClockIcon,
    customCondition: (companyInfo: CompanyInfo) => {
      return companyInfo?.hasVndlyIntegration
    },
  },
  {
    to: '/review',
    text: 'Review',
    icon: ThumbsUpIcon,
  },
  {
    to: '/workers',
    text: 'Workers',
    icon: UsersIcon,
  },
  {
    to: '/payments',
    text: 'Payments',
    icon: DollarSignIcon,
    permissionRequired: PermissionEnum.PaymentAccess,
  },
  {
    to: '/messages',
    text: 'Messages',
    icon: MessageSquareIcon,
    permissionRequired: PermissionEnum.MessageWorker,
  },
  {
    to: '/settings',
    text: 'Settings',
    icon: SettingsIcon,
  },
]

export const useNavOptions = () => {
  const { pathname } = useLocation()
  const { company } = useAuthContext()

  const enabledNavOptions = useMemo(() => {
    return navOptions.filter((navOption) => {
      if (navOption.customCondition) {
        if (!company) {
          // This means we haven't loaded the company info yet
          return false
        }
        return navOption.customCondition(company)
      }
      return true
    })
  }, [company])

  const selectedNavOption = useMemo(() => {
    return (
      navOptions.find((navOption) => {
        return !!matchPath({ path: navOption.to, end: false }, pathname)
      }) ?? null
    )
  }, [pathname])

  return { navOptions: enabledNavOptions, selectedNavOption }
}
